import { BreakpointObserver } from '@angular/cdk/layout';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  iconBreakpoint,
  xxsmallBreakpoint,
} from '@epicuro-next/ataraxia/image';
import {
  IImage,
  MediaType as MediaTypeFromIImage,
} from '@epicuro-next/ataraxia/models';
import { ProductImageMissingVisualConfig } from '@epicuro-next/ataraxia/product-image';
import { getCurrentLanguage } from '@epicuro-next/platform/i18n';
import { IDabEntry, IDabType } from '@epicuro-next/state/dab-state';
// tslint:disable-next-line:nx-enforce-module-boundaries
import {
  inputHasChanged,
  isNonNullish,
  isNullish,
} from '@epicuro-next/utilities';
import {
  MediaType,
  Product,
  ProductMedia,
} from 'graphql-api/gql-api-interfaces';
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import { map, shareReplay, switchMap, take, takeUntil } from 'rxjs/operators';

import { ProductCardActionsComponentInputArgs } from '../../interfaces/product-card-actions-component-input-args';
import { ProductCardHeaderComponentInputArgs } from '../../interfaces/product-card-header-component-input-args';
import { ProductCardIconsComponentInputArgs } from '../../interfaces/product-card-icons-component-input-args';
import { fadeInOut, slideDown } from '../animations';
import { ProductCardBaseComponent } from '../product-card-base/product-card-base.component';

@Component({
  selector: 'atx-product-card',
  templateUrl: './product-card.component.html',
  styleUrls: ['./product-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [fadeInOut, slideDown],
  standalone: false,
})
export class ProductCardComponent
  extends ProductCardBaseComponent
  implements OnDestroy, OnInit, OnChanges
{
  @Input() public extendedDabItemsEnabled = false;
  @Input() public showImgOverflow = false;
  @Input() public useThumbnailsSkeleton = true;
  @Input() public showImgFullSpace = true;

  @Input()
  public compactView = false;

  @Input()
  public set groupVariants(value: boolean) {
    this.groupVariants$.next(value);
  }
  public get groupVariants(): boolean {
    return this.groupVariants$.getValue();
  }

  @Input()
  public bookMarkNextAvailableVariant = false;

  @Input()
  public productImageMissingVisualConfig?: ProductImageMissingVisualConfig = undefined;

  @Input()
  public headerInputArgs: ProductCardHeaderComponentInputArgs = {
    idFields: ['displayedProductId'],
    titleFields: ['articleTitle'],
    showWholeSalePrice: true,
    showWspdPrice: false,
  };

  @Input()
  public iconsInputArgs: ProductCardIconsComponentInputArgs = {
    language: getCurrentLanguage(),
    showGenderIcon: false,
    dateFormat: 'MMM y',
  };

  @Input()
  public actionsInputArgs: ProductCardActionsComponentInputArgs = {
    addedToActiveList: false,
    previewAttributes: false,
    replenishments: null,
  };

  @Output() public readonly addProductToActiveList = new EventEmitter<void>();
  @Output() public readonly removeProductFromActiveList =
    new EventEmitter<void>();
  @Output() public deleteProductFromOpenedList = new EventEmitter<Product>();

  @HostBinding('attr.data-e2e') get e2e() {
    return `p-card-id-${this.product.productId}`;
  }

  public get mayUseMissingImageContent(): boolean {
    if (!this.productImageMissingVisualConfig) {
      return false;
    }

    return this.productImageMissingVisualConfig.enabled;
  }

  public readonly imageBreakpoints = [xxsmallBreakpoint];
  public readonly carouselBreakpoints = [iconBreakpoint];
  public readonly dabItemsHovered$ = new BehaviorSubject(false);

  /**
   * Method to get number of thumbnails to show in carousel
   */
  public get thumbnailsNumber(): number {
    if (this.breakpointObserver.isMatched('(min-width: 1200px)')) {
      return 4;
    }

    if (this.breakpointObserver.isMatched('(min-width: 768px)')) {
      return 3;
    }

    return 2;
  }

  public productMedia: ProductMedia[] = [];

  /**
   * Flag to show/hide the add to list toast.
   */
  public showToastMessage = false;
  public dabData$: Observable<IDabEntry[] | undefined>;
  public dabInBasketData$: Observable<IDabEntry | null>;
  public variantsMedia: ProductMedia[][];
  public showProductDetails = true;
  public groupVariants$ = new BehaviorSubject(false);
  private destroy$: Subject<void> = new Subject();

  @Input() public selectDABQuantities: ({
    productId,
    brandId,
  }: {
    productId: string;
    brandId: string;
  }) => Observable<IDabEntry[] | undefined> = () => of(undefined);

  @Input() public checkIfProductIsMarketing: (
    productId: string,
  ) => Observable<boolean> = () => of(false);

  @Input() public selectDABinBasket: (
    product: Product,
  ) => Observable<IDabEntry | null> = () => of(null);

  constructor(private readonly breakpointObserver: BreakpointObserver) {
    super();
  }

  public ngOnInit(): void {
    this.getDab();
    this.getDabInBasket();
    this.populateProductMedia(this.showVariations, this.groupVariants);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (
      inputHasChanged<ProductCardComponent>(
        changes,
        'showVariations',
        'groupVariants',
      )
    ) {
      this.groupVariants$.pipe(take(1)).subscribe((groupVariants) => {
        this.populateProductMedia(this.showVariations, groupVariants);
      });
    }
  }

  public updateCurrentVariant(index: number) {
    if (this.variantsMedia[index]) {
      this.currentVariantMedia = this.variantsMedia[index];
    }
  }

  public getProduct(index: number): Product {
    if (
      index === -1 ||
      isNullish(this.product.relatedVariants) ||
      this.product.relatedVariants.length === 0
    ) {
      return this.product;
    }
    const relatedVariantIndex = index - 1;
    const product =
      this.product.relatedVariants[relatedVariantIndex] ?? this.product;
    return product;
  }

  public onDeleteFromProductList() {
    this.deleteProductFromOpenedList.emit(this.product);
  }

  public getCarouselItems(shouldGroupVariants: boolean): IImage[] {
    if (shouldGroupVariants && !this.product.relatedVariants?.length) {
      return [this.mapToIImage(this.productMedia[0])];
    }

    const images = (
      this.productMedia?.length > 1
        ? this.productMedia
        : this.currentVariantMedia
    ).map(this.mapToIImage);

    return images;
  }

  private mapToIImage: (productMedia: ProductMedia) => IImage = (
    productMedia: ProductMedia,
  ) => {
    return {
      type: this.mapToMediaType(productMedia.type),
      url: productMedia.url,
      urls: productMedia.urls as string[] | undefined,
    };
  };

  private mapToMediaType: (mediaType: MediaType) => MediaTypeFromIImage = (
    mediaType: MediaType,
  ) => {
    switch (mediaType) {
      case MediaType.Image:
        return MediaTypeFromIImage.IMAGE;
      case MediaType.Video:
        return MediaTypeFromIImage.VIDEO;
      case MediaType.Image360:
        return MediaTypeFromIImage.IMAGE360;
      default:
        return mediaType as MediaTypeFromIImage;
    }
  };

  public getCarouselToolTip(shouldGroupVariants: boolean) {
    let itemsCount;
    if (!shouldGroupVariants && !this.product.relatedVariants?.length) {
      itemsCount = (this.productMedia?.length ?? 0) - this.thumbnailsNumber;
      return $localize`:PCard|more color images available tooltip@@PCardMoreImages:${itemsCount} more images available`;
    }
    if (this.productMedia?.length > 1) {
      itemsCount = this.productMedia?.length - this.thumbnailsNumber;
      return $localize`:PCard|more variant images available tooltip@@PCardMoreColors:${itemsCount} more colors available`;
    }
    itemsCount = this.currentVariantMedia?.length - this.thumbnailsNumber;
    return $localize`:PCard|more color images available tooltip@@PCardMoreImages:${itemsCount} more images available`;
  }

  public trackDabItem(_: number, dabItem: IDabEntry) {
    return `${dabItem.type}${dabItem.quantity}`;
  }

  public onAddProductToActiveList(): void {
    this.addProductToActiveList.emit();
  }

  public onRemoveProductFromActiveList(): void {
    this.removeProductFromActiveList.emit();
  }

  public getIDabType(type: string): IDabType {
    switch (type.toUpperCase()) {
      case 'DELIVERED':
        return IDabType.DELIVERED;
      case 'INBASKET':
        return IDabType.INBASKET;
      case 'INBOUND':
        return IDabType.INBOUND;
      default:
        return type as IDabType;
    }
  }

  public ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private populateProductMedia(
    showVariations: boolean,
    groupVariants: boolean,
  ) {
    const queryByImageType = (media: ProductMedia) =>
      media.type.toLowerCase() === MediaType.Image.toLowerCase();

    const productMedia = this.product.media || [];
    this.variantsMedia = [this.product.media];
    this.currentVariantMedia =
      groupVariants && this.product.articleImageUrl
        ? [{ type: MediaType.Image, url: this.product.articleImageUrl }]
        : this.product.media;
    let resultMedia = [];

    if (showVariations && this.product.relatedVariants?.length) {
      const initial: ProductMedia[] = [];
      const variationsMedias = this.product.relatedVariants.reduce(
        (media, variation) => {
          const variationMedia = variation.media.find(queryByImageType);
          return variationMedia ? [...media, variationMedia] : media;
        },
        initial,
      );
      resultMedia = [productMedia[0], ...variationsMedias];
    } else {
      resultMedia = productMedia;
    }

    if (this.product.relatedVariants?.length) {
      this.variantsMedia = [
        ...this.variantsMedia,
        ...this.product.relatedVariants.map((variant) => variant.media),
      ];
    }

    this.productMedia = resultMedia
      .filter(isNonNullish)
      .filter(queryByImageType);
  }

  private getDab() {
    this.dabData$ = this.groupVariants$.pipe(
      switchMap((collapse) => {
        if (!collapse || !this.product.relatedVariants) {
          return this.selectDABQuantities({
            productId: this.product.productId,
            brandId: this.product.brandId,
          });
        }

        return this.selectDABQuantities({
          productId: this.product.parentId ?? this.product.productId,
          brandId: this.product.brandId,
        }).pipe(
          map((dabs) => {
            dabs?.reduce((acc, dab) => {
              const existingDabType = acc.find(({ type }) => type === dab.type);
              if (existingDabType) {
                existingDabType.quantity += dab.quantity;
              } else {
                acc.push(dab);
              }
              return acc;
            }, [] as IDabEntry[]);
            const dabDelivered = dabs?.find(
              ({ type }) => type === IDabType.DELIVERED,
            );
            const dabInbound = dabs?.find(
              ({ type }) => type === IDabType.INBOUND,
            );

            return [dabInbound, dabDelivered].filter(Boolean) as
              | IDabEntry[]
              | undefined;
          }),
        );
      }),
      takeUntil(this.destroy$),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  private getDabInBasket() {
    this.dabInBasketData$ = this.groupVariants$.pipe(
      switchMap((collapse) => {
        if (!collapse || !this.product.relatedVariants) {
          return this.selectDABinBasket(this.product);
        }

        return combineLatest([
          this.selectDABinBasket(this.product),
          ...this.product.relatedVariants.map((variant) =>
            this.selectDABinBasket({
              ...variant,
              parent: this.product.parent,
            }),
          ),
        ]).pipe(
          map((dabs: (IDabEntry | null)[]) => {
            return dabs.filter(Boolean).reduce((dab, item) => {
              if (!dab) {
                return item;
              }

              return {
                ...dab,
                quantity: dab.quantity + (item?.quantity ?? 0),
              };
            }, null);
          }),
        );
      }),
      takeUntil(this.destroy$),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }
}
